import { Component, type ErrorInfo, type ReactNode } from 'react'

type ErrorBoundaryProps = { children: ReactNode }
type ErrorBoundaryState = { error: Error | null; diagnosticId: string | null }

function redact(value: string) {
  return value
    .replace(/(?:\/Users\/|\/home\/|[A-Za-z]:\\)[^\s)]+/g, '[path redacted]')
    .replace(/Bearer\s+[^\s]+/gi, 'Bearer [redacted]')
    .replace(/(?:sk|pk)-[A-Za-z0-9_-]{12,}/g, '[credential redacted]')
    .slice(0, 800)
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, diagnosticId: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, diagnosticId: null }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const source = `${error.message}\n${info.componentStack || ''}`
    const diagnosticId = `ui-${Date.now().toString(36)}`
    this.setState({ diagnosticId })
    console.error(`[${diagnosticId}] ${redact(source)}`)
  }

  render() {
    if (!this.state.error) return this.props.children
    const message = redact(this.state.error.message || 'The workspace failed to render.')
    return (
      <main className="fatal-error" role="alert">
        <div className="fatal-error-card">
          <span className="fatal-error-kicker">CODE WORKSPACE</span>
          <h1>Code needs to recover</h1>
          <p>The current view stopped safely. Your project files and task history were not modified by this screen.</p>
          <code>{message}</code>
          {this.state.diagnosticId && <small>Diagnostic {this.state.diagnosticId} · redacted locally</small>}
          <div className="fatal-error-actions">
            <button type="button" onClick={() => window.location.reload()}>Reload workspace</button>
            <button type="button" className="is-muted" onClick={() => this.setState({ error: null, diagnosticId: null })}>Try again</button>
          </div>
        </div>
      </main>
    )
  }
}
